import type { SVGProps } from "react";

/**
 * Inline icons used across the landing page. They are hand-drawn in the
 * design (rather than pulled from lucide) so the strokes match the artwork.
 */
type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Icon({ size = 16, children, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

export function PinIcon(props: IconProps) {
  return (
    <Icon
      strokeWidth={2}
      {...props}
    >
      <circle
        cx="12"
        cy="10"
        r="2.6"
      />
      <path d="M12 21c4.2-4.6 6.2-7.6 6.2-10.6A6.2 6.2 0 0 0 5.8 10.4C5.8 13.4 7.8 16.4 12 21Z" />
    </Icon>
  );
}

export function LockIcon(props: IconProps) {
  return (
    <Icon
      strokeWidth={2.2}
      {...props}
    >
      <rect
        x="4"
        y="10.5"
        width="16"
        height="10.5"
        rx="2.5"
      />
      <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
    </Icon>
  );
}

export function ArrowRightIcon(props: IconProps) {
  return (
    <Icon
      strokeWidth={2.2}
      {...props}
    >
      <path d="M5 12h13" />
      <path d="m12.5 6 6 6-6 6" />
    </Icon>
  );
}

export function ArrowLeftIcon(props: IconProps) {
  return (
    <Icon
      strokeWidth={2.2}
      {...props}
    >
      <path d="M19 12H6" />
      <path d="m11.5 6-6 6 6 6" />
    </Icon>
  );
}

export function ShieldIcon(props: IconProps) {
  return (
    <Icon
      strokeWidth={2.4}
      {...props}
    >
      <path d="M12 3 4 6.4v5.2c0 4.4 3.2 8.2 8 9.4 4.8-1.2 8-5 8-9.4V6.4Z" />
    </Icon>
  );
}

export function AlertCircleIcon(props: IconProps) {
  return (
    <Icon
      strokeWidth={2.2}
      {...props}
    >
      <circle
        cx="12"
        cy="12"
        r="9"
      />
      <path d="M12 7.6v5" />
      <path d="M12 16.2h.01" />
    </Icon>
  );
}

export function EyeIcon(props: IconProps) {
  return (
    <Icon
      strokeWidth={2}
      {...props}
    >
      <path d="M2.5 12S6 5.8 12 5.8 21.5 12 21.5 12 18 18.2 12 18.2 2.5 12 2.5 12Z" />
      <circle
        cx="12"
        cy="12"
        r="3"
      />
    </Icon>
  );
}

export function EyeOffIcon(props: IconProps) {
  return (
    <Icon
      strokeWidth={2}
      {...props}
    >
      <path d="M4 4l16 16" />
      <path d="M9.9 5.9A9.9 9.9 0 0 1 12 5.8c6 0 9.5 6.2 9.5 6.2a17 17 0 0 1-3 3.7" />
      <path d="M6.5 8.3A17 17 0 0 0 2.5 12S6 18.2 12 18.2c1 0 1.9-.2 2.7-.4" />
    </Icon>
  );
}

export function ChevronRightIcon(props: IconProps) {
  return (
    <Icon
      strokeWidth={2.4}
      {...props}
    >
      <path d="m9 6 6 6-6 6" />
    </Icon>
  );
}

export function CheckIcon(props: IconProps) {
  return (
    <Icon
      strokeWidth={2.6}
      {...props}
    >
      <path d="M20 6 9 17l-5-5" />
    </Icon>
  );
}

export function LayersIcon(props: IconProps) {
  return (
    <Icon
      strokeWidth={2}
      {...props}
    >
      <path d="M3 6.8 9 4l6 2.8L21 4v13.2L15 20l-6-2.8L3 20Z" />
      <path d="M9 4v13.2" />
      <path d="M15 6.8V20" />
    </Icon>
  );
}

export function BookmarkIcon(props: IconProps) {
  return (
    <Icon
      strokeWidth={2.4}
      {...props}
    >
      <path d="M6.5 3.5h11a1 1 0 0 1 1 1V21l-6.5-4-6.5 4V4.5a1 1 0 0 1 1-1Z" />
    </Icon>
  );
}

export function ClockIcon(props: IconProps) {
  return (
    <Icon
      strokeWidth={2.2}
      {...props}
    >
      <circle
        cx="12"
        cy="12"
        r="8.5"
      />
      <path d="M12 7.4V12l3 2" />
    </Icon>
  );
}

export function SearchIcon(props: IconProps) {
  return (
    <Icon
      strokeWidth={2.2}
      {...props}
    >
      <circle
        cx="11"
        cy="11"
        r="6.5"
      />
      <path d="m16 16 4.5 4.5" />
    </Icon>
  );
}

export function BellIcon(props: IconProps) {
  return (
    <Icon
      strokeWidth={2.4}
      {...props}
    >
      <path d="M18 15.5V11a6 6 0 1 0-12 0v4.5L4.5 18h15Z" />
      <path d="M10 21h4" />
    </Icon>
  );
}

export function GlobeIcon(props: IconProps) {
  return (
    <Icon
      strokeWidth={2}
      {...props}
    >
      <circle
        cx="12"
        cy="12"
        r="8.5"
      />
      <path d="M3.5 12h17" />
      <path d="M12 3.5c2.2 2.4 3.3 5.3 3.3 8.5S14.2 18.1 12 20.5c-2.2-2.4-3.3-5.3-3.3-8.5S9.8 5.9 12 3.5Z" />
    </Icon>
  );
}

export function MailIcon(props: IconProps) {
  return (
    <Icon
      strokeWidth={2}
      {...props}
    >
      <rect
        x="2.8"
        y="5"
        width="18.4"
        height="14"
        rx="2.5"
      />
      <path d="m3.4 6.6 8.6 6 8.6-6" />
    </Icon>
  );
}

export function PhoneIcon(props: IconProps) {
  return (
    <Icon
      strokeWidth={2}
      {...props}
    >
      <path d="M5.6 3.2h3.1l1.6 4-2 1.3a11 11 0 0 0 5.2 5.2l1.3-2 4 1.6v3.1a1.6 1.6 0 0 1-1.7 1.6A15.6 15.6 0 0 1 4 4.9a1.6 1.6 0 0 1 1.6-1.7" />
    </Icon>
  );
}

export function UserIcon(props: IconProps) {
  return (
    <Icon
      strokeWidth={2}
      {...props}
    >
      <circle
        cx="12"
        cy="9"
        r="3.4"
      />
      <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
      <circle
        cx="12"
        cy="12"
        r="9.2"
      />
    </Icon>
  );
}

export function MenuIcon(props: IconProps) {
  return (
    <Icon
      strokeWidth={2.2}
      {...props}
    >
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </Icon>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <Icon
      strokeWidth={2.2}
      {...props}
    >
      <path d="M6 6 18 18" />
      <path d="M18 6 6 18" />
    </Icon>
  );
}
